import React, { useState, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { Menu, FlaskConical } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';

export default function MainLayout() {
  const isDemo = useAuthStore(s => s.isDemo);
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (mobile) setCollapsed(true);
    };
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // Close mobile sidebar on route change
  useEffect(() => {
    if (mobileOpen) setMobileOpen(false);
  }, [location.pathname]);

  return (
    <div style={{ display: 'flex', height: '100vh', background: '#0a0a0f', overflow: 'hidden', fontFamily: 'DM Sans, sans-serif' }}>
      {/* Mobile overlay */}
      {isMobile && mobileOpen && (
        <div
          onClick={() => setMobileOpen(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
            zIndex: 49, backdropFilter: 'blur(2px)',
          }}
        />
      )}

      {/* Sidebar — on mobile: fixed overlay; on desktop: inline */}
      <div style={{
        position: isMobile ? 'fixed' : 'relative',
        top: 0, left: 0, bottom: 0,
        zIndex: isMobile ? 50 : 'auto',
        transform: isMobile ? (mobileOpen ? 'translateX(0)' : 'translateX(-100%)') : 'none',
        transition: 'transform 0.3s cubic-bezier(0.16,1,0.3,1)',
        flexShrink: 0,
        height: isMobile ? '100vh' : undefined,
      }}>
        <Sidebar
          collapsed={isMobile ? false : collapsed}
          onToggle={isMobile ? () => setMobileOpen(false) : () => setCollapsed(c => !c)}
        />
      </div>

      {/* Main content */}
      <main style={{
        flex: 1,
        overflowY: 'auto',
        overflowX: 'hidden',
        transition: 'all 0.3s cubic-bezier(0.16,1,0.3,1)',
        display: 'flex',
        flexDirection: 'column',
      }}>
        {/* Mobile top bar */}
        {isMobile && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 12,
            padding: '14px 20px',
            background: '#111014',
            borderBottom: '1px solid #27262f',
            position: 'sticky', top: 0, zIndex: 40,
            flexShrink: 0,
          }}>
            <button
              onClick={() => setMobileOpen(true)}
              style={{
                width: 36, height: 36, borderRadius: 9,
                background: '#1c1b22', border: '1px solid #33323d',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', color: '#9ca3af',
              }}
            >
              <Menu size={18} />
            </button>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{
                width: 28, height: 28,
                background: 'linear-gradient(135deg, #f97316, #ea580c)',
                borderRadius: 7,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 13, color: '#fff',
                boxShadow: '0 0 12px rgba(249,115,22,0.3)',
              }}>P</div>
              <span style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 14, color: '#f4f3f8', letterSpacing: '-0.02em' }}>PRINT ERP</span>
            </div>
          </div>
        )}

        {/* Demo mode banner */}
        {isDemo && (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            padding: '7px 16px',
            background: 'linear-gradient(90deg, rgba(249,115,22,0.12), rgba(249,115,22,0.08), rgba(249,115,22,0.12))',
            borderBottom: '1px solid rgba(249,115,22,0.2)',
            fontSize: 12, color: '#f97316', flexShrink: 0,
          }}>
            <FlaskConical size={13} />
            <span style={{ fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.05em' }}>
              MODO DEMO · Datos de ejemplo · Sin conexión a base de datos
            </span>
          </div>
        )}

        <div style={{ flex: 1 }}>
          <Outlet />
        </div>
      </main>
    </div>
  );
}
